<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { useDtfStore } from "../../store";
import type { Standard } from "../../types";
import Headline from "../components/Headline.vue";
import Section from "../components/Section.vue";
import { List, ListItem } from "../components/List";
import PopularBadge from "./PopularBadge.vue";
import PrintPerfectComparison from "./PrintPerfectComparison.vue";
import TrimInfo from "../../TrimInfo.vue";
import RedrawNotes from "../../RedrawNotes.vue";
import SmartRedrawNotes from "../../SmartRedrawNotes.vue";

const props = defineProps<{
  artwork: Standard.Artwork;
  reorderEnabled: boolean;
  enableAbMonetizationUiV2: boolean;
  paidExpertEditAddonEnabled?: boolean | undefined;
  index: number;
}>();

const store = useDtfStore();
const { n: numberFormatter } = useI18n();
const isExpertEditAddonChecked = ref(props.artwork.hasExpertEditAddon || false);

const isLoading = computed(() => !Number(props.artwork.pricing?.expertEditAddonTierPrice));
const expertEditAddonTierPrice = computed(() =>
  isLoading.value
    ? 0
    : n(props.artwork.pricing.expertEditAddonTierPrice, "currency", numberFormatter)
);

watch(
  () => props.artwork.hasExpertEditAddon,
  newValue => {
    isExpertEditAddonChecked.value = newValue || false;
  }
);

const showRedrawUI = computed(() => isExpertEditAddonChecked.value);
const showSmartRedrawNotes = computed(() => store.experiments.ab_smart_redraw_notes === 1);

function onExpertEditAddonToggle(checked: boolean) {
  isExpertEditAddonChecked.value = checked;

  if (props.artwork.isSimulated) {
    store.setSimulation({ hasExpertEditAddon: checked });
  } else {
    store.updateArtworkValuesById({
      id: props.artwork.id,
      hasExpertEditAddon: checked,
      enhancementNote: ""
    });
  }

  store.getArtworksPricing();

  submitDtfEvent("expert_edit_addon_toggled", {
    artwork_id: props.artwork.id,
    product_id: store.productId,
    enabled: checked,
    business_line: store.businessLine,
    catalog_number: store.catalogNumber,
    active_config_section: "full_enhancements"
  });
}
</script>

<template>
  <Headline :large-gap="reorderEnabled" class="print-perfect__headline">
    <PopularBadge />
    <span class="print-perfect__free-text">FREE</span>
    <template v-if="enableAbMonetizationUiV2" #title>Flawless prints, zero guesswork. </template>
    <template v-else #title>PrintPerfect results, with zero guesswork.</template>

    <template v-if="enableAbMonetizationUiV2" #subtitle>
      It's a no-brainer! Jiffy magic transforms any image into glorious print ready artwork.
      Retail-quality results, without the design degree.
    </template>
    <template v-else #subtitle>
      For Jiffy results you know and trust. The smartest way to print your design.
    </template>
  </Headline>

  <Section v-if="artwork.canRemoveBg" class="print-perfect__trim-section">
    <TrimInfo
      v-if="!artwork.isFile && !artwork.isSimulated"
      :id="artwork.id"
      :artwork="artwork"
      :active-config-section="enableAbMonetizationUiV2 ? 'jiffy-print-perfect' : undefined"
      :can-remove-bg="artwork.canRemoveBg"
      :remove-background="artwork.removeBackground"
      :toggle-background-error="artwork.toggleBackgroundError || ''"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
    />
  </Section>

  <Section v-if="paidExpertEditAddonEnabled" class="print-perfect__expert-edit-addon-section">
    <div class="print-perfect__addon-section">
      <div class="print-perfect__addon-checkbox">
        <label class="print-perfect__checkbox-label">
          <input
            type="checkbox"
            class="print-perfect__checkbox-input"
            :checked="isExpertEditAddonChecked"
            @change="e => onExpertEditAddonToggle((e.target as HTMLInputElement).checked)"
          />
          <span class="print-perfect__checkbox-box">
            <UseSvg
              v-if="isExpertEditAddonChecked"
              icon="check"
              class="print-perfect__checkbox-icon"
            />
          </span>
          <span class="print-perfect__addon-label">
            <div class="print-perfect__addon-label-text">
              <span>Jiffy Design Services</span>
              <span class="print-perfect__addon-badge">Add-On</span>
            </div>
            <span class="print-perfect__addon-help">
              Use our professional designers to edit your image
            </span>
          </span>
        </label>
      </div>
      <div class="print-perfect__addon-price">
        <span>{{ expertEditAddonTierPrice }}</span>
      </div>
    </div>

    <div class="js-print-perfect-redraw-notes-addon" v-if="showRedrawUI" :data-id="artwork.id">
      <SmartRedrawNotes
        v-if="showRedrawUI && showSmartRedrawNotes"
        :index="index"
        :artwork="artwork"
      />
      <RedrawNotes
        v-else-if="showRedrawUI"
        :index="index"
        :enhancement-note="artwork.enhancementNote"
      />
    </div>
  </Section>

  <Section class="print-perfect__list-section">
    <h3 class="print-perfect__list-section-heading">Benefits of PrintPerfect</h3>
    <List v-if="enableAbMonetizationUiV2" long>
      <ListItem enable-ab-monetization-ui-v2>Enhances colors, sharpens details</ListItem>
      <ListItem enable-ab-monetization-ui-v2>Vectorizes and resizes your design</ListItem>
      <ListItem enable-ab-monetization-ui-v2>Eliminates blurring and pixelation</ListItem>
      <ListItem enable-ab-monetization-ui-v2>Saves you time and effort</ListItem>
      <ListItem kind="highlight">
        <a href="/jiffy-transfers-FAQs#design-quality-guarantee" target="_blank">
          PrintPerfect Guarantee
        </a>
        included
      </ListItem>
    </List>
    <List v-else long>
      <ListItem>Retail-quality results at any size</ListItem>
      <ListItem>Advanced background removal</ListItem>
      <ListItem>Sharper edges and lines</ListItem>
      <ListItem>Crystal-clear details</ListItem>
      <ListItem kind="highlight">
        <a href="/jiffy-transfers-FAQs#design-quality-guarantee" target="_blank">
          Design Quality Guarantee
        </a>
      </ListItem>
    </List>
  </Section>

  <Section>
    <img
      v-if="enableAbMonetizationUiV2"
      class="print-perfect__comparison-img"
      src="https://jiffyimg.imgix.net/gen/expert_edit_illustration_1/expert-edit-illustrative-example.gif?ixlib=rb-0.3.5&auto=format"
    />
    <PrintPerfectComparison v-else />
  </Section>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "../ArtworkQualityConfigPanel.scss";

.print-perfect {
  &__headline {
    @include with-quality-container {
      padding-left: 60px;
      padding-right: 130px;
      margin-bottom: 20px;
    }
  }

  &__trim-section {
    margin-bottom: 8px;
  }

  &__list-section {
    flex-direction: column;
    margin-bottom: 8px;
  }

  &__list-section-heading {
    font-weight: 700;
    margin-bottom: 16px;
    text-align: center;
  }

  &__comparison-img {
    width: 100%;

    // Fix for safari
    outline-offset: -1px;
    outline: 1px solid $crafterpay-gray-modern-50;
  }

  &__expert-edit-addon-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 8px;

    > * {
      width: 100%;
    }
  }

  &__addon-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-width: 0;
  }

  &__addon-checkbox {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 12px;
  }

  &__addon-price {
    color: $price-product-card;
    font-family: Arial;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    flex: 0 0 auto;
    white-space: nowrap;

    @include with-quality-container {
      font-size: 22px;
    }
  }

  &__free-text {
    color: $green-discount-text;
    font-weight: 700;
    font-size: 20px;
    display: flex;
    flex-direction: column;
    line-height: 1;
    flex-shrink: 0;
    gap: 4px;
    position: absolute;
    right: 16px;
  }

  &__checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__checkbox-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  &__checkbox-box {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 2px solid $crafterpay-gray-modern-400;
    border-radius: 4px;
    background-color: $white;
    margin-right: 12px;
    flex-shrink: 0;
  }

  &__checkbox-input:checked + &__checkbox-box {
    background-color: $primary-light;
    border-color: $primary-light;
  }

  &__checkbox-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 18px;
    height: 18px;
    color: $white;
    pointer-events: none;
  }

  &__addon-label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__addon-label-text {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    line-height: 18px;
  }

  &__addon-badge {
    background-color: $primary-light;
    color: $white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__addon-help {
    font-size: 12px;
    font-weight: 400;
    color: $crafterpay-gray-modern-600;
    line-height: 1.4;

    @include with-quality-container {
      font-size: 14px;
    }
  }

  @include only-portable {
    &__addon-section {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 12px;
      min-width: unset;
    }

    &__addon-checkbox {
      display: contents;
      flex: unset;
      margin-right: 0;
    }

    &__checkbox-label {
      display: contents;
    }

    &__checkbox-box {
      margin-right: 0;
    }

    &__addon-label {
      justify-content: center;
      flex: unset;
      min-width: unset;
    }

    &__addon-label-text {
      font-size: 14px;
      flex-wrap: nowrap;
    }

    &__addon-badge {
      font-size: 11px;
      padding: 2px 6px;
    }

    &__addon-help {
      font-size: 12px;
    }

    &__addon-price {
      font-size: 18px;
      justify-self: end;
      flex: unset;
      white-space: unset;
    }
  }
}
</style>
